import { Organization, OrganizationOverviewFragment } from '@alice-financial/alice-sdk'
import {
  Box,
  CircularProgress,
  DataGrid,
  Grid,
  GridColDef,
  GridRenderCellParams,
  Link,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { orgRootPath } from '../orgDashboard/organization/useOrg'
import { OnboardingOrgRow } from './types'

const OrgInfoCell = ({ org }: { org: OrganizationOverviewFragment }) => {
  if (!org) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={16} />
      </Box>
    )
  }
  const displayName = org?.name || ''
  const handleOrgNameClick = (event: React.MouseEvent) => {
    event.stopPropagation() // Prevent row click
    // Let the Link component handle navigation
  }

  const orgPath = orgRootPath(org.id)
  return (
    <Box>
      <Typography variant="body1" component="div" fontWeight="bold">
        <Link to={orgPath} underline="hover" onClick={handleOrgNameClick}>
          {displayName}
        </Link>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {org.employees.enrolledCount || 0}/{org.employees.reachableCount || 0} employees enrolled •{' '}
        {org.workLocations.length || 0} locations • {org.employers.length || 0} pay groups
      </Typography>
    </Box>
  )
}

// Logo cell that uses pre-loaded data
const LogoCell = ({ org }: { org: Pick<Organization, 'logo' | 'name'> }) => {
  // Use org.name if available, otherwise fall back to adminRole data
  const displayName = org.name || ''

  return (
    <Grid container alignItems="center" justifyContent="center" width="100%" height="100%">
      {org.logo?.url && (
        <Grid
          component="img"
          src={org.logo.url}
          alt={`Logo for ${displayName}`}
          sx={{
            maxHeight: 50,
            maxWidth: 50,
            objectFit: 'contain',
          }}
        />
      )}
    </Grid>
  )
}

// Define columns for DataGrid
const createColumns = (): GridColDef<OnboardingOrgRow>[] => [
  {
    field: 'logo',
    headerName: '',
    width: 80,
    sortable: false,
    filterable: false,
    cellClassName: 'logo-cell',
    renderCell: (params: GridRenderCellParams<OnboardingOrgRow>) => <LogoCell org={params.row.org} />,
  },
  {
    field: 'orgName',
    flex: 1,
    sortable: false,
    filterable: false,
    renderHeader: () => (
      <Typography variant="subtitle2" whiteSpace="normal" textAlign="center">
        Organization
      </Typography>
    ),
    renderCell: (params: GridRenderCellParams<OnboardingOrgRow>) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <OrgInfoCell org={params.row.org} />
      </Box>
    ),
  },
]

interface OrgListDataGridProps {
  rows: OnboardingOrgRow[]
}

export const OrgListDataGrid = ({ rows }: OrgListDataGridProps) => {
  const columns = createColumns()

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      density="compact"
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableDensitySelector
      disableColumnSorting
      disableRowSelectionOnClick
      hideFooter
      getRowHeight={() => 80}
      sx={{
        '& .logo-cell': {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
        },
      }}
    />
  )
}
